if (!customElements.get('nooance-serum-cascade')) {
  customElements.define('nooance-serum-cascade', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.video = this.querySelector('video');
      this.stage = this.querySelector('.serum-cascade__stage');
      this.text = this.querySelector('.serum-cascade__text');
      this.lead = this.querySelector('.serum-cascade__lead');
      this.panels = [...this.querySelectorAll('[data-chapter]')];
      this.buttons = [...this.querySelectorAll('[data-time]')];
      this.playButton = this.querySelector('[data-play]');
      this.progress = this.querySelector('.serum-cascade__track span');
      this.status = this.querySelector('[role=status]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.duration = Number(this.dataset.duration) || 18;
      this.introEnd = Number(this.dataset.introEnd) || 0;
      this.intro = this.introEnd > 0 ? 'pending' : 'done';
      this.outroStart = Number(this.dataset.outroStart) || 0;
      this.outro = 'pending';
      this.outroFrom = this.outroStart;
      // Autoplay mode: no scroll-driven film; it plays when shown and the steps fast-forward to their moment.
      this.autoplay = this.dataset.mode === 'autoplay';
      this.target = -1;
      if (this.autoplay) {
        this.intro = 'done';
        this.outro = 'done';
        this.classList.add('is-autoplay');
      }
      this.scrollTime = this.introEnd;
      this.scrollProgress = 0;
      this.anchor = [0, this.introEnd];
      this.desired = 0;
      this.active = -1;
      this.failed = false;
      this.loaded = false;
      this.playing = false;
      this.raf = 0;
      this.manual = false;
      this.leadHeight = 0;
      this.video.controls = false;
      this.classList.add('is-enhanced');
      this.onScroll = () => this.followScroll();
      this.buttons.forEach(button => button.addEventListener('click', () => {
        if (this.autoplay) { this.seek(Number(button.dataset.time)); return; }
        this.load();
        this.pause();
        this.desired = this.clampTime(Number(button.dataset.time));
        if (!this.manual) {
          this.intro = this.desired < this.introEnd ? 'pending' : 'done';
          this.anchor = [0, this.introEnd];
          this.outro = 'pending';
          this.outroFrom = Math.max(this.outroStart, this.desired);
          this.jumping = true;
          const top = scrollY + this.getBoundingClientRect().top + this.leadHeight - this.top +
            (this.offsetHeight - this.leadHeight - this.stage.offsetHeight) * this.progressAt(this.desired);
          scrollTo({ top, behavior: 'instant' });
          cancelAnimationFrame(this.jumpFrame);
          this.jumpFrame = requestAnimationFrame(() => { this.jumping = false; this.onScroll(); this.startIntro(); });
        } else {
          scrollTo({ top: scrollY + this.getBoundingClientRect().top + this.leadHeight - this.top, behavior: 'instant' });
        }
        this.queue();
      }, { signal }));
      // Autoplay mode: no play button, a click or tap on the film pauses or resumes it.
      this.art = this.querySelector('.serum-cascade__art');
      if (this.autoplay) {
        this.art.tabIndex = 0;
        this.art.setAttribute('role', 'button');
        this.art.setAttribute('aria-label', this.playButton.dataset.playLabel);
        this.art.addEventListener('click', () => this.playButton.click(), { signal });
        this.art.addEventListener('keydown', event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.playButton.click();
        }, { signal });
      }
      if (this.autoplay) this.panels.forEach((panel, i) => {
        if (!('step' in panel.dataset) || !this.buttons[i]) return;
        panel.tabIndex = 0;
        panel.setAttribute('role', 'button');
        panel.addEventListener('click', () => this.buttons[i].click(), { signal });
        panel.addEventListener('keydown', event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.buttons[i].click();
        }, { signal });
      });
      this.playButton.addEventListener('click', async () => {
        this.load();
        if (this.playing) {
          const outro = this.outro === 'playing';
          this.pause();
          if (outro) this.outro = 'done';
          this.userPaused = true;
          return;
        }
        this.userPaused = false;
        this.finished = false;
        if (this.video.currentTime >= this.duration - .15) {
          this.desired = 0;
          this.video.currentTime = 0;
        }
        try { await this.video.play(); } catch {
          if (!signal.aborted) this.status.textContent = this.status.dataset.error;
        }
      }, { signal });
      this.video.addEventListener('play', () => {
        this.stopRewind();
        this.playing = true;
        this.setPlayLabel(this.playButton.dataset.pauseLabel);
        this.queue();
      }, { signal });
      this.video.addEventListener('pause', () => {
        if (this.intro === 'playing') this.intro = 'done';
        if (this.playing) this.desired = this.video.currentTime;
        this.playing = false;
        this.setPlayLabel(this.playButton.dataset.playLabel);
      }, { signal });
      this.video.addEventListener('ended', () => { if (this.outro === 'playing') this.outro = 'done'; this.finished = true; this.target = -1; this.pause(); this.update(this.video.currentTime); }, { signal });
      this.video.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(this.video.duration) && this.video.duration > 0) this.duration = this.video.duration;
        this.desired = this.clampTime(this.desired);
        this.queue();
      }, { signal });
      this.video.addEventListener('loadeddata', () => { this.status.textContent = ''; this.queue(); }, { signal });
      ['progress', 'canplay', 'canplaythrough'].forEach(event => this.video.addEventListener(event, () => this.queue(), { signal }));
      this.video.addEventListener('seeked', () => { this.update(this.video.currentTime); this.queue(); }, { signal });
      this.video.addEventListener('error', () => this.fallback(), { signal });
      addEventListener('scroll', this.onScroll, { passive: true, signal });
      addEventListener('resize', () => this.scheduleMeasure(), { signal });
      window.visualViewport?.addEventListener('resize', () => this.scheduleMeasure(), { signal });
      this.motion.addEventListener('change', () => { this.pause(); this.scheduleMeasure(); }, { signal });
      this.mobile.addEventListener('change', () => { this.setVariant(); this.scheduleMeasure(); }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause(); else this.onScroll(); }, { signal });
      document.addEventListener('shopify:block:select', event => {
        if (event.target.contains(this) || this.contains(event.target)) this.buttons[0].click();
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.load(); this.onScroll(); }
        else this.pause();
      }, { rootMargin: '500px 0px' });
      this.observer.observe(this.stage);
      this.introObserver = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        if (this.autoplay) {
          if (entry.isIntersecting) { this.reveal(); this.autoStart(); }
          else if (entry.boundingClientRect.top > 0) this.rewindToStart();
          return;
        }
        if (entry.isIntersecting) { this.onScroll(); this.startIntro(); }
        else if (entry.boundingClientRect.top > 0) this.resetIntro();
      }, { threshold: [0, .25, .5, .6, .75, 1] });
      this.introObserver.observe(this.stage);
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      const cartElements = [...document.querySelectorAll('add-to-cart-component')];
      [this, document.querySelector('header-component'), document.querySelector('.anchors-section'), ...cartElements]
        .filter(Boolean).forEach(element => this.resizeObserver.observe(element));
      this.cartObserver = new MutationObserver(() => this.scheduleMeasure());
      cartElements.forEach(element => this.cartObserver.observe(element, {
        attributes: true, attributeFilter: ['class', 'style', 'hidden'],
      }));
      cartElements.forEach(element => {
        element.addEventListener('transitionend', () => this.scheduleMeasure(), { signal });
        element.addEventListener('animationend', () => this.scheduleMeasure(), { signal });
      });
      document.fonts.ready.then(() => { if (!signal.aborted) this.scheduleMeasure(); });
      this.setVariant();
      this.update(0);
      this.measure();
      this.onScroll();
    }
    disconnectedCallback() {
      this.pause();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.introObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.cartObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.jumpFrame);
      cancelAnimationFrame(this.measureFrame);
    }
    followScroll() {
      const viewportHeight = Math.min(innerHeight, window.visualViewport?.height || innerHeight);
      if (Math.abs(this.bottomInset(viewportHeight) - (this.bottom || 0)) > .5) this.scheduleMeasure();
      if (this.autoplay) { this.reveal(); this.autoStart(); return; }
      if (this.failed || this.manual || this.jumping) return;
      const rect = this.getBoundingClientRect();
      this.scrollProgress = (this.top - rect.top - this.leadHeight) /
        Math.max(1, this.offsetHeight - this.leadHeight - this.stage.offsetHeight);
      this.scrollTime = this.clampTime(this.mapScroll(this.scrollProgress));
      if (this.intro === 'pending') this.startIntro();
      if (this.intro !== 'done') return;
      if (this.outroStart && !this.autoplayBlocked) {
        // Once the scroll reaches Restructure, the film plays to the end on its own, like the intro.
        if (this.outro === 'pending' && this.scrollProgress > 1) {
          // Coming back from below the film: show its last frame instead of replaying the end.
          this.outro = 'done';
          this.desired = this.clampTime(this.duration);
          this.queue();
          return;
        }
        if (this.scrollTime >= this.outroStart - .05) { this.startOutro(); return; }
        if (this.outro === 'playing' && this.scrollTime >= this.outroStart - .3) return;
        if (this.outro !== 'pending' || this.outroFrom !== this.outroStart) this.resetOutro();
      }
      this.desired = this.scrollTime;
      this.queue();
    }
    scrubEnd() {
      return this.autoplayBlocked ? this.duration : this.outroStart || this.duration;
    }
    scrubStop() {
      // Keep the stage pinned for the last stretch of scroll while the outro plays.
      return this.outroStart && !this.autoplayBlocked ? .85 : 1;
    }
    mapScroll(progress) {
      const [start, time] = this.anchor;
      if (progress >= start) return time + (progress - start) / Math.max(1e-6, this.scrubStop() - start) * (this.scrubEnd() - time);
      return start > 0 ? time * Math.max(0, progress) / start : time;
    }
    progressAt(time) {
      return Math.max(0, Math.min(1, (time - this.introEnd) / Math.max(1e-6, this.scrubEnd() - this.introEnd))) * this.scrubStop();
    }
    clampTime(time) {
      return Math.max(0, Math.min(this.duration - 1 / 24, time));
    }
    scheduleMeasure() {
      if (this.measureFrame || !this.controller || this.failed) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measure();
        this.onScroll();
        this.startIntro();
        this.queue();
      });
    }
    bottomInset(viewportHeight) {
      return [...document.querySelectorAll('add-to-cart-component')].reduce((inset, element) => {
        const style = getComputedStyle(element);
        if (style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return inset;
        const rect = element.getBoundingClientRect();
        if (rect.height <= 0 || rect.top >= viewportHeight || rect.bottom <= 0) return inset;
        return Math.max(inset, viewportHeight - Math.max(0, rect.top), rect.height + (parseFloat(style.bottom) || 0));
      }, 0);
    }
    measure() {
      const header = document.querySelector('header-component');
      const height = header?.getBoundingClientRect().height || 0;
      const anchors = document.querySelector('.anchors-section');
      const anchorStyle = anchors && getComputedStyle(anchors);
      const anchorBottom = anchors?.offsetHeight && anchorStyle.position === 'sticky'
        ? (parseFloat(anchorStyle.top) || height) + anchors.offsetHeight : 0;
      this.top = Math.max(height, anchorBottom);
      const viewportHeight = Math.min(innerHeight, window.visualViewport?.height || innerHeight);
      const bottom = this.bottomInset(viewportHeight);
      this.bottom = bottom;
      const available = Math.max(0, viewportHeight - this.top - bottom);
      this.style.setProperty('--sc-top', `${this.top}px`);
      this.style.setProperty('--sc-bottom', `${bottom}px`);
      this.style.setProperty('--sc-available-height', `${available}px`);
      this.classList.toggle('is-compact', this.mobile.matches && available < 600);
      this.leadHeight = this.lead?.offsetHeight || 0;
      const clone = this.text.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      Object.assign(clone.style, { position: 'absolute', visibility: 'hidden', left: '-10000px',
        top: '0', margin: '0', width: `${this.text.getBoundingClientRect().width}px`, height: 'auto', display: 'block' });
      Object.assign(clone.querySelector('.serum-cascade__copy').style, {
        position: 'static', transform: 'none', margin: this.mobile.matches
          ? getComputedStyle(this.querySelector('.serum-cascade__copy')).margin : `${getComputedStyle(this.text).rowGap} 0 0`,
      });
      this.stage.append(clone);
      if (!this.mobile.matches) clone.style.paddingBottom = getComputedStyle(clone).paddingTop;
      const maximum = clone.getBoundingClientRect().height;
      clone.remove();
      const safeBottom = Math.ceil(maximum + (this.mobile.matches ? 10 : 22));
      const minimum = this.mobile.matches ? safeBottom + 148 : Math.max(660, Math.ceil(maximum));
      this.style.setProperty('--sc-safe-bottom', `${safeBottom}px`);
      const manual = this.motion.matches || (!this.autoplay && available < minimum);
      this.style.setProperty('--sc-min-height', `${minimum + (manual || this.autoplay ? 64 : 0)}px`);
      if (manual !== this.manual) this.pause();
      this.manual = manual;
      this.classList.toggle('is-manual', manual);
      this.classList.toggle('is-reduced', this.motion.matches);
      this.dataset.playback = manual ? 'manual' : 'scroll';
    }
    setVariant() {
      const variant = this.mobile.matches ? 'mobile' : 'desktop';
      if (this.dataset.variant === variant && this.source) return;
      this.pause();
      this.dataset.variant = variant;
      this.source = this.pickSource();
      this.video.poster = this.mobile.matches ? this.video.dataset.mobilePoster : this.video.dataset.desktopPoster;
      if (this.loaded && !this.failed) {
        this.video.src = this.source;
        this.video.load();
      }
    }
    pickSource() {
      const light = this.mobile.matches || this.slowNetwork();
      const source = light ? this.video.dataset.mobileSrc : this.video.dataset.desktopSrc;
      return source.startsWith('/cdn/') && this.dataset.shopDomain
        ? new URL(source, `https://${this.dataset.shopDomain}`).href : source;
    }
    slowNetwork() {
      const connection = navigator.connection;
      if (connection) return Boolean(connection.saveData || /2g|3g/.test(connection.effectiveType || '') ||
        (connection.downlink > 0 && connection.downlink < 5));
      // Safari and Firefox: estimate from the poster, which is always fetched before the film.
      const poster = performance.getEntriesByName(this.video.poster)[0];
      if (!poster?.transferSize || poster.responseEnd <= poster.responseStart) return false;
      return poster.transferSize * 8 / (poster.responseEnd - poster.responseStart) / 1000 < 5;
    }
    load() {
      if (this.loaded || this.failed) return;
      this.loaded = true;
      this.source = this.pickSource();
      this.video.preload = 'auto';
      this.video.src = this.source;
      this.video.load();
    }
    startIntro() {
      if (this.intro !== 'pending' || this.manual || this.failed) return;
      this.anchor = [0, this.introEnd];
      if (this.mapScroll(Math.max(0, this.scrollProgress)) - this.introEnd > 2) { this.intro = 'done'; this.onScroll(); return; }
      const rect = this.stage.getBoundingClientRect();
      if (Math.min(innerHeight, rect.bottom) - Math.max(0, rect.top) < rect.height * .55) return;
      this.introProgress = Math.max(0, this.scrollProgress);
      this.load();
      this.intro = 'playing';
      this.desired = 0;
      this.video.muted = true;
      if (this.video.currentTime > 0) this.video.currentTime = 0;
      this.video.play().catch(error => {
        if (this.intro !== 'playing') return;
        if (error?.name === 'NotAllowedError') this.autoplayBlocked = true;
        this.intro = 'done';
        this.anchor = [this.introProgress, 0];
        this.onScroll();
      });
    }
    setPlayLabel(label) {
      this.playButton.textContent = label;
      if (this.autoplay) this.art.setAttribute('aria-label', label);
    }
    visibleEnough() {
      const rect = this.stage.getBoundingClientRect();
      return Math.min(innerHeight, rect.bottom) - Math.max(0, rect.top) >= Math.min(rect.height, innerHeight) * .5;
    }
    reveal() {
      // The three steps fade up, one after the other, once the text comes into view.
      if (this.classList.contains('is-revealed')) return;
      if (this.querySelector('.serum-cascade__copy').getBoundingClientRect().top < innerHeight * .85) this.classList.add('is-revealed');
    }
    rewindToStart() {
      // Scrolled back above the film: start again from the drop next time it shows up.
      if (this.userPaused || this.failed) return;
      this.pause();
      this.target = -1;
      this.finished = false;
      this.desired = 0;
      if (this.video.readyState >= 1) this.video.currentTime = 0;
      this.update(0);
    }
    autoStart() {
      if (!this.autoplay || this.rewinding || this.manual || this.failed || this.autoplayBlocked || this.userPaused ||
        this.finished || this.playing || !this.visibleEnough()) return;
      this.load();
      this.video.muted = true;
      this.playing = true;
      this.video.play().catch(error => {
        this.playing = false;
        if (error?.name === 'NotAllowedError') this.autoplayBlocked = true;
      });
    }
    seek(time) {
      this.load();
      this.stopRewind();
      this.userPaused = false;
      this.finished = false;
      time = this.clampTime(time);
      if (time < this.video.currentTime - .4 && !this.motion.matches && this.video.readyState >= 1) {
        this.rewind(time);
        return;
      }
      if (time > this.video.currentTime + .4 && !this.motion.matches) {
        // Fast-forward to the chosen step, then carry on at normal speed.
        this.target = time;
      } else {
        this.target = -1;
        this.desired = time;
        this.video.currentTime = time;
      }
      this.video.muted = true;
      this.video.play().catch(() => {
        this.target = -1;
        this.desired = time;
        this.queue();
      });
      this.queue();
    }
    rewind(time) {
      // Browsers cannot play backwards: step the film back frame by frame, fast, then play on from the step.
      this.pause();
      this.target = -1;
      const rate = Math.max(4, (this.video.currentTime - time) / 2);
      let last = performance.now();
      const step = now => {
        if (!this.rewinding) return;
        this.rewinding = requestAnimationFrame(step);
        if (this.video.seeking) return;
        const next = Math.max(time, this.video.currentTime - (now - last) / 1000 * rate);
        last = now;
        this.desired = next;
        this.video.currentTime = next;
        this.update(next);
        if (next > time) return;
        this.stopRewind();
        this.video.play().catch(() => this.queue());
      };
      this.rewinding = requestAnimationFrame(step);
    }
    stopRewind() {
      cancelAnimationFrame(this.rewinding);
      this.rewinding = 0;
    }
    startOutro() {
      if (this.outro !== 'pending' || this.manual || this.failed) return;
      const rect = this.stage.getBoundingClientRect();
      if (Math.min(innerHeight, rect.bottom) - Math.max(0, rect.top) < rect.height * .55) return;
      this.load();
      this.outro = 'playing';
      this.desired = this.clampTime(this.outroFrom);
      this.video.muted = true;
      if (Math.abs(this.video.currentTime - this.desired) > 1 / 48) this.video.currentTime = this.desired;
      this.video.play().catch(error => {
        if (this.outro !== 'playing') return;
        this.playing = false;
        if (error?.name === 'NotAllowedError') {
          // Autoplay refused (iOS Low Power Mode…): let the scroll drive the film to the end instead.
          this.autoplayBlocked = true;
          this.outro = 'pending';
          this.anchor = [this.scrollProgress, this.desired];
          this.onScroll();
          return;
        }
        // Interrupted (seek, source change…): try again on the next frame.
        this.outro = 'pending';
        requestAnimationFrame(() => this.onScroll());
      });
    }
    resetOutro() {
      if (this.outro === 'playing') this.pause();
      this.outro = 'pending';
      this.outroFrom = this.outroStart;
    }
    resetIntro() {
      if (!this.introEnd || this.manual || this.failed) return;
      this.pause();
      this.outro = 'pending';
      this.outroFrom = this.outroStart;
      this.intro = 'pending';
      this.anchor = [0, this.introEnd];
      this.desired = 0;
      this.queue();
    }
    finishIntro() {
      this.intro = 'done';
      this.pause();
      this.desired = this.scrollTime;
      this.onScroll();
      this.queue();
    }
    pause() {
      if (!this.video) return;
      this.stopRewind();
      if (this.intro === 'playing') this.intro = 'done';
      if (this.outro === 'playing') {
        this.outro = 'pending';
        this.outroFrom = Math.max(this.outroStart, this.video.currentTime);
      }
      if (this.video.playbackRate !== 1) this.video.playbackRate = 1;
      if (this.playing) this.desired = this.video.currentTime;
      if (!this.video.paused) this.video.pause();
      this.playing = false;
      this.setPlayLabel(this.playButton.dataset.playLabel);
    }
    queue() {
      if (this.raf || !this.controller || this.failed) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        if (this.playing) {
          this.desired = this.video.currentTime;
          this.update(this.desired);
          const target = Math.max(this.introEnd, Math.min(this.scrollTime, this.scrubEnd()));
          if (this.intro === 'playing' && this.desired >= target - 1 / 48) this.finishIntro();
          else {
            // When the visitor scrolls ahead of the intro, play faster until the film catches up.
            if (this.target >= 0 && this.desired >= this.target - 1 / 48) this.target = -1;
            const rate = this.target >= 0 ? 4
              : this.intro === 'playing' && target > this.introEnd + .5 && target - this.desired > 1.5 ? 3 : 1;
            if (this.video.playbackRate !== rate) this.video.playbackRate = rate;
            this.queue();
          }
        } else if (this.video.readyState >= 2 && !this.video.seeking) {
          if (Math.abs(this.video.currentTime - this.desired) > 1 / 48) {
            const ranges = this.video.seekable;
            for (let i = 0; i < ranges.length; i++) {
              if (this.desired >= ranges.start(i) && this.desired <= ranges.end(i)) {
                this.video.currentTime = this.desired;
                break;
              }
            }
          } else this.update(this.video.currentTime);
        }
      });
    }
    update(time) {
      const index = this.panels.reduce((last, panel, i) => time >= Number(panel.dataset.start) ? i : last, 0);
      if (index !== this.active) {
        this.active = index;
        const result = this.panels[index].dataset.chapter === 'resultat';
        this.panels.forEach((panel, i) => {
          panel.toggleAttribute('data-active', i === index || (result && 'step' in panel.dataset));
          if (i === index && 'step' in panel.dataset) panel.setAttribute('aria-current', 'step');
          else panel.removeAttribute('aria-current');
        });
        this.buttons.forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
        this.dataset.chapter = this.panels[index].dataset.chapter;
        const current = this.buttons[index];
        const nav = current.parentElement;
        if (current.offsetLeft < nav.scrollLeft || current.offsetLeft + current.offsetWidth > nav.scrollLeft + nav.clientWidth) {
          nav.scrollTo({ left: Math.max(0, current.offsetLeft - nav.offsetLeft - 8), behavior: 'instant' });
        }
      }
      this.progress.style.transform = `scaleX(${Math.min(1, time / this.duration)})`;
      // Each step's line fills up while its part of the film plays.
      this.panels.forEach((panel, i) => {
        if (!('step' in panel.dataset)) return;
        const start = Number(panel.dataset.start);
        const end = Number(this.panels[i + 1]?.dataset.start ?? this.duration);
        const progress = Math.max(0, Math.min(1, (time - start) / Math.max(.01, end - start))).toFixed(3);
        if (panel.style.getPropertyValue('--sc-step-progress') !== progress) panel.style.setProperty('--sc-step-progress', progress);
      });
    }
    fallback() {
      this.failed = true;
      this.pause();
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.measureFrame);
      this.raf = 0;
      this.measureFrame = 0;
      this.classList.remove('is-enhanced', 'is-manual', 'is-reduced', 'is-compact');
      this.video.controls = true;
      this.status.textContent = this.status.dataset.error;
    }
  });
}
